import { useState, useEffect, useCallback } from "react";
import Head from "next/head";
import { AppProps } from "next/app";
import { useRouter } from "next/router";
import AppContext from "src/components/AppContext";
import { userService } from "src/services";
import "public/styles/consumer.scss";
import { UserObject, validateUser } from "src/helpers";
import ConsumerNewLayout from "src/components/layouts/ConsumerNewLayout";
import { Alert } from "src/components";
import { SocketProvider } from "src/components/SocketContext";
import "../public/styles/index.css";
import { getPageMeta } from "src/constants/pageMeta";
import {
  captureCmsParamsFromQuery,
  getCmsSubscriptionRedirectPath,
  getPlanIdFromQuery,
  isCmsCheckoutCapturePath,
  isRazorpayPaymentReturnQuery,
  urlHasCmsPlaintextParams,
} from "src/helpers/coupon-client";

const PUBLIC_PATHS = [
  "/refund-policy",
  "/shipping-and-delivery-policy",
  "/testimonials",
  "/api-docs",
  "/unsubscribe",
  // "/register",
];

const CONSUMER_PATHS = [
  "/consumer",
  "/dashboard",
  "/analyze-stock",
  "/pattern-insights",
  "/sector-momentum",
  "/portfolio",
  "/watchlist",
  "/subscription",
  "/subscription/payment-gateway",
  "/payment-success",
  "/profile",
  "/settings",
  "/refund-policy",
  "/shipping-and-delivery-policy",
  "/testimonials",
  "/order-block",
  "/notifications",
  "/insider-trading",
  "/billing",
  "/news",
  "/pastPerformance",
  "/screener",
  "/portfolio/[portfolio_id]/timeline",
  "/support",
];

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const [currentUser, setCurrentUser] = useState<UserObject | undefined>();
  const [darkMode, setDarkMode] = useState(true);
  // Start as false to prevent protected pages from rendering while auth check runs
  const [authorized, setAuthorized] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const checkAuthAndSubscription = useCallback(
    (url: string) => {
      const path = url.split("?")[0];
      const user: UserObject = userService.userValue;
      setCurrentUser(user);

      if (path === "/") {
        setAuthorized(true);
        return;
      }

      const isAllowed =
        PUBLIC_PATHS.includes(path) ||
        CONSUMER_PATHS.includes(path) ||
        /^\/portfolio\/.*\/timeline$/.test(path) ||
        path.startsWith("/support/");

      if (!user && isAllowed && !PUBLIC_PATHS.includes(path)) {
        localStorage.setItem("referer", path);
      }

      // Auth logic
      let isAuthValid = true;
      if (!user && !PUBLIC_PATHS.includes(path)) {
        isAuthValid = false;
        router.push("/");
      } else if (user && !isAllowed) {
        isAuthValid = false;
        router.push("/");
      }

      // Subscription logic
      if (user && isAuthValid && isAllowed && !PUBLIC_PATHS.includes(path)) {
        validateUser();
        if (
          !user.is_subscribed &&
          !user.is_free &&
          ![
            "/profile",
            "/subscription",
            "/subscription/payment-gateway",
            "/support",
          ].some((p) => path.startsWith(p))
        ) {
          isAuthValid = false;
          router.push(getCmsSubscriptionRedirectPath() || "/subscription");
        } else if (
          (user.is_subscribed || user.is_free) &&
          path.startsWith("/subscription") &&
          !path.includes("payment-gateway")
        ) {
          isAuthValid = false;
          router.push("/");
        }
      }

      setAuthorized(isAuthValid);
    },
    [router],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const userStr = localStorage.getItem("BetaUser");
      const storedTheme = localStorage.getItem("dark");
      if (userStr) setCurrentUser(JSON.parse(userStr));
      setDarkMode(storedTheme === "true" || storedTheme === null);
    } catch (err) {
      console.error("Failed to sync from localStorage", err);
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    const user = userService.userValue;
    const pathname = router.pathname || "/";

    // Capture CMS params into storage and strip plaintext from the URL (no API).
    // Never treat payment-gateway verify URLs (they also carry plan_id) as CMS links.
    if (
      isCmsCheckoutCapturePath(pathname) &&
      getPlanIdFromQuery(router.query) &&
      !isRazorpayPaymentReturnQuery(router.query) &&
      (!user || !user.is_subscribed)
    ) {
      captureCmsParamsFromQuery(router.query, pathname);
      if (urlHasCmsPlaintextParams(router.asPath, router.query)) {
        if (pathname === "/" || pathname === "") {
          void router.replace("/", undefined, { shallow: true });
        } else if (pathname === "/subscription") {
          void router.replace("/subscription", undefined, { shallow: true });
        }
      }
    }

    checkAuthAndSubscription(router.asPath);
  }, [
    isInitialized,
    checkAuthAndSubscription,
    router.query,
    router.asPath,
    router,
  ]);

  // Removed separate subscriptionCheck effect as it is now consolidated

  // Force light mode on api-docs page only for unauthenticated users
  useEffect(() => {
    const path = router.pathname;
    if (path === "/api-docs") {
      // Only force light mode if user is NOT authenticated
      const user = userService.userValue;
      if (!user) {
        // Force light mode for unauthenticated users
        setDarkMode(false);
        // Remove dark class from document
        if (typeof window !== "undefined") {
          document.documentElement.classList.remove("dark");
          const lightBg = "#edf1fc";
          document.documentElement.style.setProperty(
            "background-color",
            lightBg,
            "important",
          );
          if (document.body) {
            document.body.style.setProperty(
              "background-color",
              lightBg,
              "important",
            );
          }
          const nextRoot = document.getElementById("__next");
          if (nextRoot) {
            nextRoot.style.setProperty(
              "background-color",
              lightBg,
              "important",
            );
          }
        }
      }
      // If user is authenticated, let them use their preferred theme (don't force anything)
    }
  }, [router.pathname, currentUser]);

  console.log(
    "[App] Rendering. authorized:",
    authorized,
    "currentUser:",
    !!currentUser,
  );

  const { title, description } = getPageMeta(router.pathname);

  return (
    <>
      <Head>
        <title>{title}</title>
        {description ? <meta name="description" content={description} /> : null}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-status-bar-style" content="black" />
        <link rel="icon" type="image/x-icon" href="/images/favicon.png" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
      </Head>

      <AppContext.Provider
        value={{
          state: {
            currentUser: currentUser,
            setShowLoader: setShowLoader,
            showLoader,
            darkMode,
            setDarkMode,
          },
        }}
      >
        <SocketProvider>
          <ConsumerNewLayout>
            <Alert />
            {authorized ||
            PUBLIC_PATHS.includes(router.pathname) ||
            router.pathname === "/" ? (
              <Component {...pageProps} />
            ) : null}
          </ConsumerNewLayout>
        </SocketProvider>
      </AppContext.Provider>
    </>
  );
};
export default App;
